import { useEffect, useRef } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

const PlacesAutocomplete = ({ value, onChange, placeholder, testId }) => {
  const containerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !containerRef.current) return;
    const container = containerRef.current;  // stable snapshot for cleanup

    // Clear any previous child elements safely
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const autocomplete = new places.PlaceAutocompleteElement({});

    // Restrict to India only
    autocomplete.includedRegionCodes = ["in"];

    // Restrict results to a rectangle covering Bengaluru + Sarathi-X's full
    // outstation serve area (Mysuru, Hosur, Nandi Hills, Ramanagara, Bidadi
    // and the airport). This is a hard restriction — places outside this box
    // (Murthal/Murdeshwar/Muradabad/Murshidabad etc.) will NOT appear.
    // If we expand outstation coverage later, widen these bounds.
    autocomplete.locationRestriction = {
      north: 14.5,   // ~north of Nandi Hills / Chikkaballapur
      south: 11.5,   // ~south of Mysuru
      west: 76.0,    // ~west of Mysuru
      east: 78.5,    // ~east of Hosur into Andhra border
    };

    autocomplete.style.width = "100%";
    autocomplete.style.height = "auto";

    const handler = async (event) => {
      try {
        const placePrediction = event.placePrediction;
        if (!placePrediction) return;

        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ["displayName", "formattedAddress", "location"] });

        // displayName can be a string or {text, languageCode} object in new API
        const rawName = place.displayName;
        const name = typeof rawName === "string" ? rawName : (rawName?.text || rawName?.toString() || "");
        const address = place.formattedAddress || "";
        const displayText = name && name !== address ? `${name}, ${address}` : (address || name);

        const coords = place.location ? {
          lat: place.location.lat(),
          lng: place.location.lng(),
        } : null;

        onChangeRef.current(displayText, coords);
      } catch (err) {
        // Fallback: try to get text from the input element itself
        const input = container.querySelector("input");
        if (input && input.value) {
          onChangeRef.current(input.value, null);
        }
      }
    };

    autocomplete.addEventListener("gmp-select", handler);
    container.appendChild(autocomplete);

    return () => {
      autocomplete.removeEventListener("gmp-select", handler);
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [places]);

  // Sync the parent-driven `value` into the live input element. Without
  // this, dragging the map (which reverse-geocodes a new address and
  // calls setAddress in the parent) leaves the search input stuck showing
  // the ORIGINAL autocomplete-selected text — and operators reasonably
  // believe the pickup location never updated. PlaceAutocompleteElement
  // may not have rendered its <input> yet on first paint, so we retry on
  // the next tick.
  useEffect(() => {
    if (typeof value !== "string") return undefined;
    const container = containerRef.current;
    if (!container) return undefined;
    const apply = () => {
      const input = container.querySelector("input");
      if (input && input.value !== value) {
        input.value = value;
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    };
    apply();
    const timer = setTimeout(apply, 50);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div
      ref={containerRef}
      className="places-autocomplete-wrapper"
      data-testid={testId}
      data-placeholder={placeholder}
    />
  );
};

export default PlacesAutocomplete;
